import type { SmartSort } from '@iheartjane/dm-sdk';
import { Placement } from '@iheartjane/dm-sdk';
import type { CustomImageAdProduct } from '@iheartjane/menu';
import { createContext, useCallback, useContext, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { useParams } from 'react-router';

import { useFetchCustomMultiPlacements } from '@jane/dm/internal';
import { useSearchContext } from '@jane/search/hooks';
import type { AlgoliaProduct } from '@jane/search/types';
import type { MenuCategoryPath } from '@jane/shared/types';

import { useMenuAdSearchFilter } from '../menuFilters/useMenuAdSearchFilter';
import { useMenu } from '../menuProvider/menuProvider';

export interface MenuAdsContextValue {
  imageAd: SmartSort<CustomImageAdProduct> | undefined;
  isError: boolean;
  isLoading: boolean;
  menuTopRow: SmartSort<AlgoliaProduct> | undefined;
}

const MenuAdsContext = createContext<MenuAdsContextValue | undefined>(
  undefined
);
MenuAdsContext.displayName = 'MenuAds';

export const useMenuAds = () => useContext(MenuAdsContext);

export const MenuAdsProvider = ({ children }: PropsWithChildren) => {
  const { store } = useMenu();
  const { searchState } = useSearchContext();
  const { category = 'all' } = useParams<{ category: MenuCategoryPath }>();

  const { searchFilter } = useMenuAdSearchFilter(category);

  const { placementViewModels, isError, isLoading } =
    useFetchCustomMultiPlacements({
      enabled: !searchState.searchText,
      storeId: Number(store.id),
      placements: [
        { placement: Placement.MenuTopRow, searchFilter },
        { placement: Placement.CustomImageRow, searchFilter },
      ],
    });

  const findRow = useCallback(
    (placement: Placement): SmartSort<AlgoliaProduct> | undefined => {
      for (const viewModel of placementViewModels) {
        if (
          viewModel.isPlacement &&
          viewModel.placement.placement === placement
        ) {
          return viewModel.placement;
        }
      }

      return undefined;
    },
    [placementViewModels]
  );

  const imageAd = useMemo(
    () =>
      findRow(Placement.CustomImageRow) as
        SmartSort<CustomImageAdProduct> | undefined,
    [findRow]
  );

  const menuTopRow = useMemo(() => findRow(Placement.MenuTopRow), [findRow]);

  const contextValue = useMemo(
    () => ({
      imageAd,
      isError,
      isLoading,
      menuTopRow,
    }),
    [imageAd, isError, isLoading, menuTopRow]
  );

  return (
    <MenuAdsContext.Provider value={contextValue}>
      {children}
    </MenuAdsContext.Provider>
  );
};
